import React from "react";

//  Import  icons (from src/assets/)
import solar from "../assets/pvt.jpg";
import MixedStorage from "../assets/Mixed Storage.jpg";
import speicher from "../assets/speicher.jpg";
import waterheater from "../assets/waterheater.jpg";
import heatpump from "../assets/heatpump.jpg";
import shower from "../assets/DHW.jpg";
import ZirkulationPump from "../assets/Zirkulation_pumpe.jpg";
import LadeSpeicher from "../assets/Ladespeicher.jpg";
import Heizung from "../assets/Heizung.jpg";
import zusatzHeizung from "../assets/Backup.jpg";
import Drei_Weg_Ventil from "../assets/3_Weg_Ventil.jpg";

import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";

/*─────────────────────────────────────────────────────────
  SVG Input (editable label + editable value)
─────────────────────────────────────────────────────────*/
function SvgEditableInput({
  x, y, label, value,
  onChangeValue,
  Width = 58, height = 40,
  align = "left",
  tooltipTitle,
  unit = "°C",
  readOnly = false,
}) {
  const ax =
    align === "center" ? x - Width / 2 : align === "right" ? x - Width : x;
  const extraW = tooltipTitle ? 20 : 0;

  return (
    <g>
      <foreignObject x={ax} y={y} width={Width + extraW} height={height}>
        <div xmlns="http://www.w3.org/1999/xhtml" className="svg-field">
          <div className="svg-field-labelRow">
            <div className="svg-field-label svg-field-label--locked">{label}</div>
            {tooltipTitle && <InfoTooltip title={tooltipTitle} />}
          </div>

          <div className="svg-field-row">
            {readOnly ? (
              <div className="svg-field-value" style={{ padding: 0 }}>
                {value ?? ""}
              </div>
            ) : (
              <input
                className="svg-field-value"
                value={value ?? ""}
                onChange={(e) => onChangeValue?.(e.target.value)}
              />
            )}
            <span className="svg-field-unit">{unit}</span>
          </div>
        </div>
      </foreignObject>
    </g>
  );
}

export const InfoTooltip = ({ title }) => (
  <Tooltip
    title={title}
    arrow
    disablePortal // ✅ CRITICAL
    PopperProps={{
      modifiers: [
        {
          name: "preventOverflow",
          options: {
            boundary: "viewport",
          },
        },
      ],
    }}
  >
    <InfoIcon
      fontSize="small"
      sx={{
        ml: 0.5,
        verticalAlign: "middle",
        cursor: "help",
        color: "#111827",
      }}
    />
  </Tooltip>
);

/*─────────────────────────────────────────────────────────
  Schematic symbols
─────────────────────────────────────────────────────────*/
function PumpSymbol({
  x,
  y,
  size = 26,
  href,
  angle = 0, // degrees: 0, 90, 180, 270 (or any)
  flipX = false, // mirror horizontally
  flipY = false, // mirror vertically
}) {
  const sx = flipX ? -1 : 1;
  const sy = flipY ? -1 : 1;

  return (
    <g
      transform={`translate(${x} ${y}) scale(${sx} ${sy}) rotate(${angle}) translate(${-x} ${-y})`}
    >
      <image
        href={href}
        x={x - size / 2}
        y={y - size / 2}
        width={size}
        height={size}
        preserveAspectRatio="xMidYMid meet"
      />
    </g>
  );
}

function ValveIconSymbol({ x, y, size = 28, href }) {
  return (
    <image
      href={href}
      x={x - size / 2}
      y={y - size / 2}
      width={size}
      height={size}
      preserveAspectRatio="xMidYMid meet"
    />
  );
}

function Icon({ href, x, y, w, h }) {
  return <image href={href} x={x} y={y} width={w} height={h} />;
}

/*─────────────────────────────────────────────────────────
  Main diagram (Formik-backed)
─────────────────────────────────────────────────────────*/
export function HeatingSystemDiagramImage1({ formik, wpResults, pvtArea, heatingRod }) {
  const setNum = (field, raw) => {
    if (raw === "") return formik.setFieldValue(field, "");
    const n = Number(raw);
    if (!Number.isNaN(n)) formik.setFieldValue(field, n);
  };
  const versorgungskonzept = formik?.values?.versorgungskonzept || "storageDHW";

  const isWohnungsstation = versorgungskonzept === "wohnungsstation";
  const isFrischwasserstation = versorgungskonzept === "frischwasserstation";
  const isStorageDHW = versorgungskonzept === "storageDHW";

  const hasCirculation = formik?.values?.circulationLosses === "yes";

  return (
    <div className="hsd-card">
      {/* Local styles for this component */}
      <style>{`
        .hsd-card{
          background:#fff;
          border-radius:16px;
          padding: clamp(8px, 1.2vw, 16px);
          box-shadow: 0 14px 40px rgba(0,0,0,0.12);
          width: 100%;
          min-width: 0;
          box-sizing: border-box;
        }
        .hsd-title{
          text-align:center;
          margin:8px 0 6px;
          font-size:clamp(1.2rem, 2vw, 2rem);
          letter-spacing:0.2px;
          color:#111827;
        }
        .hsd-sub{
          text-align:center;
          margin:0 0 14px;
          font-size:clamp(0.75rem, 1vw, 0.95rem);
          color:#6b7280;
        }

        /* SVG embedded inputs */
        .svg-value-input{
          width:56px;
          height:22px;
          font-size:12px;
          border:1px solid #9ca3af;
          border-radius:4px;
          padding:0 6px;
          box-sizing:border-box;
          background:#fff;
        }
        
      .svg-field{
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        border-radius: 0.5em;
        border: 1px solid #d1d5db;
        background: rgba(255,255,255,0.92);
        box-shadow: 0 2px 10px rgba(0,0,0,0.10);
        padding: 0.28em 0.42em;
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 0.15em;
        overflow: visible;
        font-size: 12px;
      }

      .svg-field-label{
        width: 100%;
        border: 0;
        outline: none;
        background: transparent;
        font-size: 0.78em;
        line-height: 1.1;
        color: #0f0f0f;
        padding: 0;
      }

      .svg-field-row{
        display: flex;
        align-items: baseline;
        gap: 0.35em;
      }

      .svg-field-value{
        flex: 1;
        min-width: 0;
        border: 0;
        outline: none;
        background: transparent;
        font-size: 1em;
        line-height: 1.1;
        font-weight: 600;
        color: #111827;
        padding: 0;
      }

      .svg-field-unit{
        font-size: 0.78em;
        color: #0f0f0f;
        user-select: none;
      }

      .svg-field:focus-within{
        border-color: #60a5fa;
        box-shadow: 0 0 0 3px rgba(96,165,250,0.25), 0 2px 10px rgba(0,0,0,0.10);
      }

      .svg-field-label--locked {
      display: block;
      width: 100%;
      box-sizing: border-box;
      padding: 2px 6px;
      border: 1px solid transparent; /* or same as input if you want */
      line-height: 1.2;
      user-select: none;
      pointer-events: none;
    }

    .svg-field-labelRow{
      display:flex;
      align-items:center;
      gap:8px;
    }

    .svg-tooltip{
      position:relative;
      display:inline-flex;
      align-items:center;
      flex:0 0 auto;
    }

    /* the icon button */
    .svg-tooltip-btn{
      width:16px;
      height:16px;
      border-radius:999px;
      border:1px solid #9ca3af;
      background:#fff;
      color:#111827;
      font-size:11px;
      line-height:16px;
      font-weight:700;
      padding:0;
      flex:0 0 auto;
      cursor:help;
      font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; /* ✅ avoids weird glyph */
    }

    /* the bubble */
    .svg-tooltip-bubble{
      position:absolute;
      z-index:9999;
      min-width:160px;
      max-width:260px;
      padding:8px 10px;
      border-radius:10px;
      border:1px solid #e5e7eb;
      background:#111827;
      color:#fff;
      font-size:11px;
      line-height:1.25;
      box-shadow:0 10px 30px rgba(0,0,0,0.25);
      opacity:0;
      pointer-events:none;
      transform:translateY(-4px);
      transition:opacity .12s ease, transform .12s ease;
      white-space:normal;
    }

    /* show on hover/focus */
    .svg-tooltip:hover .svg-tooltip-bubble,
    .svg-tooltip:focus-within .svg-tooltip-bubble{
      opacity:1;
      transform:translateY(0);
    }

    /* placements */
    .svg-tooltip--top .svg-tooltip-bubble{
      bottom:125%;
      right:0;
    }
    .svg-tooltip--right .svg-tooltip-bubble{
      left:125%;
      top:50%;
      transform:translateY(-50%);
    }
    .svg-tooltip--bottom .svg-tooltip-bubble{
      top:125%;
      right:0;
    }
    .svg-tooltip--left .svg-tooltip-bubble{
      right:125%;
      top:50%;
      transform:translateY(-50%);
    }
    .hsd-svgWrap{
      width: 100%;
      min-width: 0;
    }
    .hsd-svg{
      display: block;
      width: 100%;
      height: auto;
      max-width: 100%;
    }

      `}</style>

      <h2 className="hsd-title">Schematische Darstellung</h2>
      <div className="hsd-sub">Raumheizung und Trinkwarmwasser</div>

      <div className="hsd-svgWrap">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1000 620"
          preserveAspectRatio="xMidYMid meet"
          className="hsd-svg"
          role="img"
          aria-label="Heating system schematic"
        >
          <defs>
            <style>{`
              .hot  { stroke: #dc2626; stroke-width: 3; fill: none; }
              .cold { stroke: #2563eb; stroke-width: 3; fill: none; }
              .circ { stroke: #10b981; stroke-width: 3; fill: none; }
              .solar { stroke: #10b981; stroke-width: 3; fill: none; stroke-dasharray: 8 6; }
              .dothot { stroke: #de4545ff; stroke-width: 3; fill: none; stroke-dasharray: 8 6; }
              .auxR { stroke: #ec842fff; stroke-width: 2.5; fill: none; }
              .auxB { stroke: #3b82f6; stroke-width: 2.5; fill: none; stroke-dasharray: 8 6; }
            `}</style>
          </defs>

          {/* =======================
              PIPES (schema style)
            ======================= */}

          {/* Solar loop (dashed green) left side */}
          <path className="solar" d="M 115 135 V 360 H 245" />
          <PumpSymbol
            href={ZirkulationPump}
            x={115}
            y={240}
            angle={270}
            size={26}
          />

          {/* solid green to dashed green through 3 weg ventil (dotted green)*/}
          <path className="solar" d="M 200 280  V 360" />

          {/* Solar to unit (solid green) */}
          <path className="circ" d="M 150 135 V 275 H 245" />
          <ValveIconSymbol href={Drei_Weg_Ventil} x={200} y={275} size={28} />

          {/* zusatzheizung to red and blue (dotted green and red line) */}
          <path className="auxB" d="M 435 200 V 355" />
          <path className="dothot" d="M 400 200 V 290" />

          {/* Main hot supply trunk from unit to tanks (red) */}
          <path className="hot" d="M 367 290 H 580" />
      
          {/* Branch down to RH-speicher hot in (red) */}
          <path className="hot" d="M 520 290 V 445 H 600 " />

          {/* DHW circulation return  from T_zapf back to TWW right side */}
          <path className="cold" d="M 820 325 V 225 H 870" />

          {/* Cold water feed  entering right side */}
          <path className="cold" d="M 875 325 H 655" />
          <text x={937} y={325} textAnchor="end" fontSize="12" fill="#374151">
            Kaltwasser
          </text>

          {/* Heating circuit to heizung (red supply) */}
          <path className="hot" d="M 625 445 H 825" />
          <ValveIconSymbol href={Drei_Weg_Ventil} x={755} y={447} size={28} />
          <PumpSymbol href={ZirkulationPump} x={700} y={445} angle={0} size={26}/>
          <path className="cold" d="M 755 520 V 458" />

          {/* Heating circuit return (blue) from heizung back to main return */}
          <path className="cold" d="M 885 520 H 625" />

          {/*green line from tww speicher back to unit */}
          <path className="cold" d="M 600 356 H 367 " />
          <PumpSymbol href={ZirkulationPump} x={405} y={356} angle={180} size={26}/>

          {/* Main return line (blue) back to unit */}
          <path className="cold" d="M 650 520 H 490 V 356 " />

          {/* Icons */}
          <Icon href={solar} x={70} y={18} w={160} h={120} />
          <Icon href={heatpump} x={225} y={255} w={160} h={130} />
          <Icon href={zusatzHeizung} x={340} y={120} w={170} h={105} />
          
          <text x={620} y={395} textAnchor="middle" fontSize="12" fill="#111827">{/* TWW-Speicher */}</text>
          <Icon href={LadeSpeicher} x={580} y={395} w={75} h={170} />
          <Icon href={shower} x={835} y={150} w={95} h={130} />
          <Icon href={Heizung} x={780} y={395} w={175} h={140} />

          {isWohnungsstation && (
            <>
              {/* tww_Speicher to T_zapf(shower) hot in (red)  */}
              <path className="hot" d="M 630 300 V 200 H 840" />
              <path className="cold" d="M 710 260 V 285 H 650" />
              <path className="cold" d="M 740 255 V 255 H 820" />

              {hasCirculation && (
                <>
                  <path className="auxR" d="M 700 240 H 790 V 200" />
                  <PumpSymbol href={ZirkulationPump} x={760} y={240} angle={180} size={26} />
                </>
              )}
              {!hasCirculation && (
                <PumpSymbol href={ZirkulationPump} x={760} y={200} angle={0} size={26} />
              )}
              <Icon href={speicher} x={580} y={230} w={75} h={170} />
              <Icon href={waterheater} x={700} y={150} w={40} h={150} />
              <PumpSymbol href={ZirkulationPump} x={650} y={200} angle={360} size={26}/>
            </>
          )}

          {isFrischwasserstation && (
            <>
              {/* tww_Speicher to T_zapf(shower) hot in (red)  */}
              <path className="hot" d="M 700 300 V 200 H 840" />
              <path className="hot" d="M 650 260 V 270 H 690" />
              <PumpSymbol href={ZirkulationPump} x={670} y={270} angle={0} size={26}/>

              {hasCirculation && (
                <>
                  <path className="auxR" d="M 730 290 H 750 V 200" />
                  <PumpSymbol href={ZirkulationPump} x={750} y={240} angle={90} size={26} />
                </>
              )}
              {!hasCirculation && (
                <PumpSymbol href={ZirkulationPump} x={760} y={200} angle={0} size={26} />
              )}
              <Icon href={speicher} x={580} y={230} w={75} h={170} />
              <Icon href={waterheater} x={690} y={220} w={40} h={150} />
            </>
          )}

          {isStorageDHW && (
            <>
              {/* tww_Speicher to T_zapf(shower) hot in (red)  */}
              <path className="hot" d="M 630 270 V 200 H 840" />

              <Icon href={MixedStorage} x={580} y={230} w={75} h={170} />

              {hasCirculation && (
                <>
                  <path className="auxR" d="M 655 290 H 710 V 200" />
                  <PumpSymbol href={ZirkulationPump} x={710} y={240} angle={90} size={26} />
                </>
              )}
              {!hasCirculation && (
                <PumpSymbol href={ZirkulationPump} x={760} y={200} angle={0} size={26} />
              )}
            </>
          )}


          {/* =======================
              Editable Inputs (Formik)
            ======================= */}

          {/* Tank temps (label editable + value editable) */}
          <SvgEditableInput
            x={500}
            y={245}
            //label="T_Sp_TWW"
            value={formik.values.dhwStorageTemp} //backend key
            //readOnly
            onChangeValue={(v) => {
              setNum("dhwStorageTemp", v); // backend key match
            }}
            tooltipTitle="Speicher-Sollwerttemperatur des Trinkwarmwasserspeichers."
          />

          <SvgEditableInput
            x={500}
            y={470}
            //label="T_Sp_RH"
            // value={formik.values.shStorageTemp} // backend key
            value={formik.values.shStorageTemp}
            //readOnly
            onChangeValue={(v) => setNum("shStorageTemp", v)}
            tooltipTitle="Speicher-Sollwerttemperatur des Raumheizungsspeichers."
          />

          {/* Heating */}
          <SvgEditableInput
            x={685}
            y={390}
            //label="T_Heiz_Ein"
            value={formik.values.flowTemp}
            //readOnly
            onChangeValue={(v) => {
              setNum("flowTemp", v);
              //setNum("shstorageTemp", v);
              }}
            tooltipTitle="Die Vorlauf- und Rücklauftemperaturen des Heizkreises."
          />
          <SvgEditableInput
            x={690}
            y={540}
            //label="T_Heiz_Aus"
            value={formik.values.returnTemp}
            //readOnly
            onChangeValue={(v) => setNum("returnTemp", v)}
            tooltipTitle="Temperatur des Heizwassers nach Wärmeabgabe im Gebäude."
          />

          {/* Cold water */}
          <SvgEditableInput
            x={915}
            y={335}
            align="right"
            //label="T_KW"
            value={formik.values.coldWaterTemp}
            readOnly
            //onChangeValue={(v) => setNum("coldWaterTemp", v)}
            tooltipTitle="Temperatur des Kaltwassers (z.B. 10 °C)."
          />

          {/*  NEW: T_Zapf (tap temperature) near shower */}
          <SvgEditableInput
            x={835}
            y={264}
            //label="T_Zapf"
            value={formik.values.pwhTemp}
            //readOnly
            onChangeValue={(v) => {
              setNum("pwhTemp", v); // UI only
              setNum("dhwStorageTemp", v); // affects backend calc
            }}
          />

          {/* NEW: T_Zirk (circulation temperature) on green return line */}
          {/* T_Zirk only for Frischwasserstation / Speicher mit Wärmeübertrager */}
          {/* {!isWohnungsstation && (
            <SvgEditableInput
              x={715}
              y={270}
              //label="T_Zirk"
              value={formik.values.circulationTemp}
              //readOnly
              onChangeValue={(v) => setNum("circulationTemp", v)} //  backend expects T_Zirk
            />
          )} */}

          <SvgEditableInput
            x={250}
            y={400}
            label="WP_Leistung"
            value={(wpResults?.requiredHeatPumpPower ?? 0).toFixed(2)}
            unit="kW"
            readOnly
            tooltipTitle="Erforderliche Wärmepumpenleistung."
            Width={100}
            height={38}
          />

          <SvgEditableInput
            x={580}
            y={378}
            label="TWW_Speicher"
            value={(wpResults?.dhwStorageSize ?? 0).toFixed(0)}
            unit="L"
            readOnly
            Width={100}
            height={38}
          />

          <SvgEditableInput
            x={580}
            y={545}
            label="RH_Speicher"
            value={(wpResults?.rhStorageSize ?? 0).toFixed(0)}
            unit="L"
            readOnly
            Width={100}
            height={38}
          />

          {/* Erforderliche PVT Fläche (above solar) */}
          <SvgEditableInput
            x={235}
            y={1}
            label="Erforderliche PVT Fläche"
            value={Number(pvtArea ?? 0).toFixed(2)}
            unit="m²"
            readOnly
            Width={160}
            height={44}
          />

          {/* Erforderliche Heizstab (above zusatzheizung) */}
          <SvgEditableInput
            x={355}
            y={80}
            label="Erforderliche Heizstab"
            value={Number(heatingRod ?? 0).toFixed(2)}
            unit="kW"
            readOnly
            Width={160}
            height={34}
          />

          {/* House outline */}
          <path
            d="M 40 165 L 505 20 L 960 150"
            fill="none"
            stroke="#9a4b4b"
            strokeWidth="6"
            strokeLinecap="round"
          />
          <path
            d="M 70 165 L 70 595 L 940 595 L 940 150"
            fill="none"
            stroke="#7c6a11"
            strokeWidth="4"
          />
        </svg>
      </div>
    </div>
  );
}
